import React from "react"
import clsx from "clsx"
import { Typography } from "@material-ui/core"
import { makeStyles, createStyles } from "@material-ui/core/styles"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHourglass } from "@fortawesome/pro-light-svg-icons/faHourglass"
import { faLocationDot } from "@fortawesome/pro-light-svg-icons/faLocationDot"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { ILunchSubscriptionCardColors, ILunchSubscriptionDetails } from "shared/Modules/UserCards/userCardsTypes"
import Card from "shared/Components/Card/Card"
import { getUsagePeriodMessageId } from "./lunchSubscriptionCardUtils"

type IClassesProps = {
    lunchSubscription?: ILunchSubscriptionCardColors
}

const DEFAULT_CARD_BACKGROUND = "#dbeafe"
const DEFAULT_TITLE_COLOR = "#000000DE"
const DEFAULT_TEXT_COLOR = "#00000099"

const useStyles = makeStyles((theme) =>
    createStyles({
        card: {
            display: "flex",
            flexDirection: "column",
        },
        lunchSubscriptionCardContent: ({ lunchSubscription }: IClassesProps) => ({
            backgroundColor: lunchSubscription?.background || DEFAULT_CARD_BACKGROUND,
            flex: 1,
            display: "flex",
            flexDirection: "column",
        }),
        topSection: {
            flex: 1,
            padding: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.375rem",
            borderBottom: `1px dashed ${theme.palette.divider}`,
        },
        bottomSection: {
            padding: "0.5rem 1rem",
            display: "flex",
            flexDirection: "column",
        },
        lunchSubscriptionHeadline: ({ lunchSubscription }: IClassesProps) => ({
            display: "block",
            color: lunchSubscription?.displayName || DEFAULT_TITLE_COLOR,
            fontWeight: 600,
            margin: 0,
        }),
        usageRow: {
            display: "flex",
            alignItems: "center",
        },
        usageBadge: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            borderRadius: "0.25rem",
            padding: "0.125rem 0.25rem",
            marginRight: "0.375rem",
            fontSize: "0.75rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
        },
        usageLabel: {
            fontSize: "0.75rem",
            fontWeight: 400,
            color: DEFAULT_TEXT_COLOR,
        },
        infoRow: {
            display: "flex",
            alignItems: "flex-start",
            gap: "0.375rem",
            fontSize: "0.75rem",
            fontWeight: 400,
        },
        dateColor: ({ lunchSubscription }: IClassesProps) => ({
            color: lunchSubscription?.subscriptionDateLabel || DEFAULT_TEXT_COLOR,
        }),
        merchantColor: ({ lunchSubscription }: IClassesProps) => ({
            color: lunchSubscription?.merchantDisplayName || DEFAULT_TEXT_COLOR,
        }),
        infoIcon: {
            fontSize: "0.75rem",
            color: "currentColor",
        },
    }),
)

type LunchSubscriptionCardProps = ILunchSubscriptionDetails

export function LunchSubscriptionCard(props: LunchSubscriptionCardProps) {
    const {
        content: { displayName, merchantDisplayName, subscriptionDateLabel, subscriptionDate },
        usageDetails,
        colors,
    } = props

    const classes = useStyles({
        lunchSubscription: colors,
    })

    const periodLabelId = usageDetails ? getUsagePeriodMessageId(usageDetails.usagePeriod) : null
    const subscriptionDateText = [subscriptionDateLabel, subscriptionDate].filter(Boolean).join(" ")

    return (
        <Card boxShadow="none" width="100%" minHeight="120px" cardClassName={classes.card}>
            <div className={classes.lunchSubscriptionCardContent}>
                <div className={classes.topSection}>
                    <Typography variant="body1" className={classes.lunchSubscriptionHeadline}>
                        {displayName}
                    </Typography>
                    {usageDetails && (
                        <div className={classes.usageRow}>
                            <LocalizedStrict
                                id="home-user-card-lunch-subscription-used-count"
                                vars={{ used: usageDetails.used, total: usageDetails.limit }}
                            >
                                <span className={classes.usageBadge}>
                                    {usageDetails.used} of {usageDetails.limit}
                                </span>
                            </LocalizedStrict>
                            {periodLabelId && (
                                <LocalizedStrict id={periodLabelId}>
                                    <span className={classes.usageLabel}>used this period</span>
                                </LocalizedStrict>
                            )}
                        </div>
                    )}
                    {subscriptionDateText && (
                        <div className={clsx(classes.infoRow, classes.dateColor)}>
                            <FontAwesomeIcon icon={faHourglass} className={classes.infoIcon} />
                            <span>{subscriptionDateText}</span>
                        </div>
                    )}
                </div>
                {merchantDisplayName && (
                    <div className={classes.bottomSection}>
                        <div className={clsx(classes.infoRow, classes.merchantColor)}>
                            <FontAwesomeIcon icon={faLocationDot} className={classes.infoIcon} />
                            <span>{merchantDisplayName}</span>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    )
}
